import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import { setInitialTab } from 'lib/functions/setInitialTab';
import { validateCompanyTypes, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthState } from 'services/redux/authSlice';

import InvoicesNotConfirmed from './components/common/InvoicesNotConfirmed';
import LastBaknrruptcyPosts from './components/common/LastBaknrruptcyPosts';
import Prospects from './components/common/Prospects';
import SiiResponsabilities from './components/common/SIIResponsabilities';
import TGRGrouped from './components/common/TGRGrouped';
import DashboardADC from './components/DashboardADC';
import MyInvoices from './components/Factoring/Invoices';
import MyWallet from './components/Factoring/Wallet';
import PublicMarket from './components/PublicMarket';

interface Tab {
  mainPage: string;
  label: string;
  component: React.JSX.Element;
  disabled?: boolean;
}

const Home: React.FC = () => {
  const { page } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [companyTabs, setCompanyTabs] = useState<Tab[]>([]);
  const [startValue, setStartValue] = useState<number | undefined>(undefined);

  const adcTabs = [
    { label: 'Dashboard', component: <DashboardADC />, mainPage: 'dashboard' },
    {
      label: 'Responsabilidades SII',
      component: <SiiResponsabilities />,
      mainPage: 'siiResponsabilities',
    },
    {
      label: 'Facturas por confirmar',
      component: <InvoicesNotConfirmed />,
      mainPage: 'notConfirmed',
    },
    { label: 'TGR', component: <TGRGrouped />, mainPage: 'tgr' },
    {
      label: 'Boletín Concursal',
      component: <LastBaknrruptcyPosts />,
      mainPage: 'bankruptcy',
    },
  ];

  const factoringTabs = {
    dashboard: {
      label: 'Cartera',
      component: <MyWallet />,
      mainPage: 'wallet',
    },
    invoices: {
      label: 'Facturas',
      component: <MyInvoices />,
      mainPage: 'lastInvoices',
    },
    tgr: { label: 'TGR', component: <TGRGrouped />, mainPage: 'tgr' },
    prospects: {
      label: 'Prospectos',
      component: <Prospects />,
      mainPage: 'prospects',
    },
    bankruptcy: {
      label: 'Boletín Concursal',
      component: <LastBaknrruptcyPosts />,
      mainPage: 'bankruptcy',
    },
    publicMarket: {
      label: 'Mercado Público',
      component: <PublicMarket />,
      mainPage: 'publicMarket',
    },
  };

  const navigate = useNavigate();

  const handleNavigation = (index: number) => {
    navigate(`/home/${companyTabs[index].mainPage}`);
  };

  const setUpHome = async () => {
    setLoading(true);
    let newCompanyTabs: Tab[] = [];
    if (validateCompanyTypes(user, 'Accountant')) {
      newCompanyTabs = adcTabs;
    }
    if (
      validateCompanyTypes(user, 'FactoringSII') ||
      validateCompanyTypes(user, 'FactoringQuoting') ||
      validateCompanyTypes(user, 'FactoringFS')
    ) {
      if (validatePermissions(user, 'sii_read')) {
        newCompanyTabs.push(factoringTabs.dashboard);
        newCompanyTabs.push(factoringTabs.invoices);
      }
      if (validatePermissions(user, 'tgr_read')) {
        newCompanyTabs.push(factoringTabs.tgr);
      }
      if (validatePermissions(user, 'prospects_read')) {
        newCompanyTabs.push(factoringTabs.prospects);
      }
      newCompanyTabs.push(factoringTabs.bankruptcy);
    }
    if (validatePermissions(user, 'public_market_read')) {
      newCompanyTabs.push(factoringTabs.publicMarket);
    }
    setInitialTab(setCompanyTabs, setStartValue, page, newCompanyTabs, 'home');
    setLoading(false);
  };

  useEffect(() => {
    void setUpHome();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <EnhancedTab
        title={`Hola ${user?.name}!`}
        startValue={startValue}
        tabs={companyTabs}
        onTabChange={handleNavigation}
      />
    </div>
  );
};

export default Home;
